import React, { useState } from 'react';
import { Row, Col, Form, Button, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
    Lock, 
    Eye, 
    EyeOff, 
    ShieldCheck, 
    AlertTriangle,
    KeyRound
} from 'lucide-react';
import { setOAuthPassword } from '../services/authService';
import { useAuth } from '../context/AuthContext';

const SetPasswordPage = () => {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [generalError, setGeneralError] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear validation errors for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        if (name === 'password') {
            calculatePasswordStrength(value);
        }
    };

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 6) strength++;
        if (password.length >= 8) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        setPasswordStrength(strength);
    };

    const getPasswordStrengthClass = () => {
        if (passwordStrength <= 2) return 'strength-weak';
        if (passwordStrength <= 3) return 'strength-fair';
        if (passwordStrength <= 4) return 'strength-good';
        return 'strength-strong';
    };

    const getPasswordStrengthText = () => {
        if (!formData.password) return 'Password must be 6-100 characters long';
        if (passwordStrength <= 2) return 'Weak password';
        if (passwordStrength <= 3) return 'Fair password';
        if (passwordStrength <= 4) return 'Good password';
        return 'Strong password';
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }
        
        if (!formData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setGeneralError(null);

        try {
            // Update password in Supabase and backend
            await setOAuthPassword(formData.password);
            
            // Refresh user session state so isPasswordSet becomes true
            await refreshUser();
            
            // Navigate to dashboard after success
            navigate('/dashboard', { replace: true });
        } catch (error) {
            console.error('Failed to set password:', error);
            setGeneralError(error.message || 'Failed to establish password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-body">
            <div className="auth-container">
                <Row className="min-vh-100 g-0">
                    {/* Left Side - Brand & Info */}
                    <Col lg={6} className="d-none d-lg-flex">
                        <div className="auth-brand-section">
                            <div className="auth-brand-overlay"></div>
                            <div className="auth-brand-content">
                                <div className="brand-logo-large">
                                    <h1>
                                        <span className="brand-name">Zpluse</span>{' '}
                                        <span className="brand-suffix">Jobs Finder</span>
                                    </h1>
                                    <p className="brand-tagline">
                                        Google login successful! Set a password to protect your account.
                                    </p>
                                </div>
                                
                                <div className="auth-features">
                                    <div className="feature-item">
                                        <div className="feature-icon">
                                            <ShieldCheck size={24} />
                                        </div>
                                        <div className="feature-text">
                                            <h6>Enhanced Security</h6>
                                            <p>Create a backup direct login using your email & password</p>
                                        </div>
                                    </div>
                                    <div className="feature-item">
                                        <div className="feature-icon">
                                            <KeyRound size={24} />
                                        </div>
                                        <div className="feature-text">
                                            <h6>Flexible Sign In</h6>
                                            <p>Access your profile anytime via Google or direct email credentials</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    
                    {/* Right Side - Form */}
                    <Col lg={6}>
                        <div className="auth-form-section">
                            <div className="auth-form-container">
                                <div className="mobile-brand d-lg-none text-center mb-4">
                                    <h3>
                                        <span className="brand-name">Zpluse</span>{' '}
                                        <span className="brand-suffix">Jobs Finder</span>
                                    </h3>
                                </div>
                                
                                <div className="auth-form-header text-center">
                                    <div className="login-avatar mb-3 text-primary">
                                        <KeyRound size={48} className="mx-auto" />
                                    </div>
                                    <h2 className="auth-title">Set Account Password</h2>
                                    <p className="auth-subtitle text-muted">
                                        Please set a password for <strong>{user?.email}</strong> to complete account setup.
                                    </p>
                                </div>
                                
                                {generalError && (
                                    <div className="alert alert-danger alert-custom alert-error d-flex align-items-center mb-4" role="alert">
                                        <AlertTriangle className="me-2" size={18} style={{ minWidth: '18px' }} />
                                        <span>{generalError}</span>
                                    </div>
                                )}
                                
                                <Form onSubmit={handleSubmit} className="auth-form" noValidate>
                                    {/* Password Field */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>New Password</Form.Label>
                                        <InputGroup>
                                            <span className="input-group-text">
                                                <Lock size={18} />
                                            </span>
                                            <Form.Control
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                placeholder="Enter new password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                isInvalid={!!validationErrors.password}
                                                isValid={formData.password && passwordStrength >= 3}
                                                className="modern-input"
                                                required
                                            />
                                            <Button 
                                                variant="outline-secondary" 
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="d-flex align-items-center"
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </Button>
                                        </InputGroup>
                                        
                                        {formData.password && (
                                            <div className="password-strength">
                                                <div className={`password-strength-bar ${getPasswordStrengthClass()}`}></div>
                                            </div>
                                        )}
                                        <small className={`text-muted ${passwordStrength <= 2 && formData.password ? 'text-danger' : passwordStrength <= 3 && formData.password ? 'text-warning' : formData.password ? 'text-success' : ''}`}>
                                            {getPasswordStrengthText()}
                                        </small>
                                        
                                        {validationErrors.password && (
                                            <div className="invalid-feedback d-block">
                                                {validationErrors.password}
                                            </div>
                                        )}
                                    </Form.Group>
                                    
                                    {/* Confirm Password Field */}
                                    <Form.Group className="mb-4">
                                        <Form.Label>Confirm Password</Form.Label>
                                        <InputGroup>
                                            <span className="input-group-text">
                                                <Lock size={18} />
                                            </span>
                                            <Form.Control
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                name="confirmPassword"
                                                placeholder="Re-enter password"
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                isInvalid={!!validationErrors.confirmPassword}
                                                isValid={formData.confirmPassword && formData.password === formData.confirmPassword && formData.confirmPassword.length >= 6}
                                                className="modern-input"
                                                required
                                            />
                                            <Button 
                                                variant="outline-secondary" 
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="d-flex align-items-center"
                                            >
                                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </Button>
                                        </InputGroup>
                                        {validationErrors.confirmPassword && (
                                            <div className="invalid-feedback d-block">
                                                {validationErrors.confirmPassword}
                                            </div>
                                        )}
                                    </Form.Group>
                                    
                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="auth-btn w-100 d-flex align-items-center justify-content-center py-2"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <div className="loading-spinner me-2" style={{ width: '1rem', height: '1rem' }}></div>
                                        ) : null}
                                        {isLoading ? 'Saving Password...' : 'Save Password & Continue'}
                                    </Button>
                                </Form>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default SetPasswordPage;
